import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Phone,
  UserCircle2,
  Brain,
  ClipboardCheck,
  Trophy,
  Activity,
  ShieldCheck,
  Sparkles,
  Download,
} from "lucide-react";
import { loadHealth } from "@/lib/healthStore";
import {
  loadNeuro,
  aggregate,
  beforeAfter,
  recoveryTimeline,
  CATEGORIES,
} from "@/lib/neuroStore";

const Card = ({ title, accent = "cyan", children, testId }) => {
  const border =
    accent === "cyan"
      ? "border-cyan-400/20"
      : accent === "violet"
      ? "border-violet-400/20"
      : accent === "emerald"
      ? "border-emerald-400/20"
      : "border-white/10";
  const text =
    accent === "cyan"
      ? "text-cyan-300"
      : accent === "violet"
      ? "text-violet-300"
      : accent === "emerald"
      ? "text-emerald-300"
      : "text-zinc-300";
  return (
    <div
      data-testid={testId}
      className={`rounded-3xl border ${border} bg-white/[0.03] p-6`}
    >
      <div className={`text-[10px] uppercase tracking-widest font-semibold ${text}`}>
        {title}
      </div>
      {children}
    </div>
  );
};

const Stat = ({ label, val, color = "#fff" }) => (
  <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div
      className="mt-1 font-display text-2xl font-semibold tabular-nums"
      style={{ color }}
    >
      {val}
    </div>
  </div>
);

export default function Profile() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [health, setHealth] = useState(() => loadHealth());
  const [neuro, setNeuro] = useState(() => loadNeuro());

  useEffect(() => {
    const onFocus = () => {
      setHealth(loadHealth());
      setNeuro(loadNeuro());
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  const agg = useMemo(() => aggregate(neuro), [neuro]);
  const rows = useMemo(() => beforeAfter(neuro), [neuro]);
  const timeline = useMemo(() => recoveryTimeline(neuro), [neuro]);
  const funCount = (neuro.functional_sessions || []).length;
  const fbkCount = (neuro.feedback || []).length;

  const download = () => {
    const payload = {
      exported_at: new Date().toISOString(),
      health_profile: health.profile,
      last_metrics: health.today,
      streak: health.streak,
      cognitive_summary: {
        baseline: neuro.baseline,
        levels: neuro.levels,
        aggregate: agg,
        before_after: rows,
      },
      functional_sessions: neuro.functional_sessions || [],
      feedback: neuro.feedback || [],
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "silentscan-recovery-profile.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const p = health.profile || {};
  const t = health.today || {};

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(45% 30% at 15% 8%, rgba(0,229,255,0.12) 0%, transparent 60%), radial-gradient(35% 25% at 90% 90%, rgba(167,139,250,0.25) 0%, transparent 60%)",
        }}
      />

      {/* Top bar */}
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/dashboard"
            data-testid="profile-back-dashboard"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <a
            href="tel:108"
            data-testid="profile-emergency"
            className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-black text-xs font-semibold px-3 py-1.5"
          >
            <Phone className="w-3.5 h-3.5" />
            Emergency 108
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-10">
        {/* Hero */}
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
            Chapter 15 · My Profile
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight"
          >
            My Health &<br />
            <span className="italic text-violet-300">Recovery</span> Profile.
          </motion.h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            One page. Everything you and a trusted caregiver need to see —
            preventive health metrics, cognitive-training progress, and
            recovery trends. Stored on your device.
          </p>
        </div>

        {/* Identity */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4">
          <Card title="Identity" accent="cyan" testId="profile-identity">
            <div className="mt-3 flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400/30 to-violet-500/20 border border-white/10 flex items-center justify-center">
                <UserCircle2 className="w-8 h-8 text-cyan-200" />
              </div>
              <div>
                <div className="font-display text-xl font-semibold">
                  {p.name || "Anonymous user"}
                </div>
                <div className="text-xs text-zinc-400">
                  {p.dob ? `DOB · ${p.dob}` : "DOB not set"}
                </div>
                <div className="text-xs text-zinc-500">
                  {p.height ? `${p.height} cm` : "—"} ·{" "}
                  {p.weight ? `${p.weight} kg` : "—"}
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                to="/medical-id"
                data-testid="profile-open-medical-id"
                className="text-xs rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-white"
              >
                Edit Medical ID
              </Link>
              <button
                data-testid="profile-download"
                onClick={download}
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400/15 px-3 py-1.5 text-cyan-100"
              >
                <Download className="w-3 h-3" />
                Export JSON
              </button>
            </div>
          </Card>

          <Card title="Today's vitals" accent="emerald" testId="profile-vitals">
            <div className="mt-3 grid grid-cols-3 gap-2">
              <Stat label="Steps" val={t.steps || 0} color="#67e8f9" />
              <Stat
                label="Sleep"
                val={t.sleep_hours ? `${t.sleep_hours}h` : "—"}
                color="#a78bfa"
              />
              <Stat
                label="HR"
                val={t.heart_rate ? `${t.heart_rate} bpm` : "—"}
                color="#f87171"
              />
              <Stat
                label="SpO₂"
                val={t.spo2 ? `${t.spo2}%` : "—"}
                color="#34d399"
              />
              <Stat
                label="Water"
                val={t.water_ml ? `${t.water_ml} ml` : "—"}
                color="#22d3ee"
              />
              <Stat
                label="Mood"
                val={t.mood || "—"}
                color="#fbbf24"
              />
            </div>
          </Card>
        </div>

        {/* Cognitive summary */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-4">
          <Card title="Cognitive training summary" accent="violet" testId="profile-cognitive">
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Sessions" val={agg.total_sessions} color="#22d3ee" />
              <Stat label="Accuracy" val={`${agg.accuracy}%`} color="#34d399" />
              <Stat
                label="Best"
                val={`${agg.personal_best}%`}
                color="#f472b6"
              />
              <Stat
                label="Consistency"
                val={`${agg.consistency}%`}
                color="#67e8f9"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <div
                  key={c.key}
                  className="rounded-full border border-white/10 bg-black/30 px-3 py-1 text-[11px] flex items-center gap-2"
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: c.accent }}
                  />
                  <span className="text-zinc-300">{c.label}</span>
                  <span
                    className="tabular-nums font-semibold"
                    style={{ color: c.accent }}
                  >
                    L{neuro.levels?.[c.key] || 1}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-xs text-zinc-500">
              Baseline taken:{" "}
              {neuro.baseline?.at
                ? new Date(neuro.baseline.at).toLocaleDateString()
                : "not yet"}
            </div>
          </Card>

          <Card
            title="Real-life training"
            accent="emerald"
            testId="profile-functional"
          >
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Stat label="Sessions" val={funCount} color="#34d399" />
              <Stat label="Feedback logs" val={fbkCount} color="#f472b6" />
            </div>
            <p className="mt-3 text-xs text-zinc-400 leading-relaxed">
              Shopping-list recall, medication routine and day-scheduling
              exercises count here. Task-performance only.
            </p>
            <Link
              to="/neurorehab"
              data-testid="profile-open-neurorehab"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-400 text-black font-medium px-4 py-2 text-xs hover:bg-emerald-300"
            >
              <ClipboardCheck className="w-3.5 h-3.5" />
              Open Real-Life Training
            </Link>
          </Card>
        </div>

        {/* Before vs After compact */}
        <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] p-6" data-testid="profile-beforeafter">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
            Before vs After (last 7 days)
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {rows.map((r) => {
              const up = r.delta > 0;
              const flat = r.delta === 0;
              return (
                <div
                  key={r.key}
                  className="rounded-2xl border border-white/10 bg-black/30 p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                      {r.label}
                    </div>
                    <div
                      className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${
                        up
                          ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                          : flat
                          ? "border-white/10 bg-white/5 text-zinc-300"
                          : "border-amber-400/40 bg-amber-400/10 text-amber-200"
                      }`}
                    >
                      {r.delta > 0 ? "+" : ""}
                      {r.delta} pp
                    </div>
                  </div>
                  <div className="mt-2 flex items-baseline gap-3 text-sm">
                    <span className="text-zinc-500 tabular-nums">
                      {r.before}%
                    </span>
                    <span className="text-zinc-500">→</span>
                    <span
                      className="font-display text-xl font-semibold tabular-nums"
                      style={{ color: r.accent }}
                    >
                      {r.after}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Safety footer */}
        <div className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-100 leading-relaxed">
            This profile is a self-tracking summary. It is <b>not</b> a medical
            record and does <b>not</b> diagnose or treat any condition. Share
            with your clinician — they always take precedence.
          </div>
        </div>

        {/* Nav shortcuts */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            {
              to: "/dashboard",
              Icon: Sparkles,
              label: "Dashboard",
              color: "#22d3ee",
            },
            {
              to: "/neurorehab",
              Icon: Brain,
              label: "NeuroRehab",
              color: "#a78bfa",
            },
            {
              to: "/simulator",
              Icon: Activity,
              label: "Future Simulator",
              color: "#34d399",
            },
            {
              to: "/impact",
              Icon: Trophy,
              label: "Impact",
              color: "#fbbf24",
            },
          ].map((it) => (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`profile-shortcut-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] p-4 flex items-center gap-3"
            >
              <div
                className="w-9 h-9 rounded-xl border flex items-center justify-center"
                style={{
                  borderColor: `${it.color}60`,
                  background: `${it.color}20`,
                }}
              >
                <it.Icon className="w-4 h-4" style={{ color: it.color }} />
              </div>
              <span className="text-sm text-white">{it.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
